import { Injectable, signal, computed } from '@angular/core';

// === INTERFACES ===
// Exportamos las interfaces para poder usarlas en toda la app
export interface Product {
  id: string;
  name: string;
  category: 'ron' | 'whisky' | 'accesorios' | 'experiencia';
  price: number;
  image_url: string;
  badge?: string;
}

export interface CartItem extends Product {
  quantity: number;
}

@Injectable({
  providedIn: 'root' // Singleton: Una única instancia de datos para toda la aplicación
})
export class CartService {

  // === ESTADO REACTIVO PRINCIPAL (Signals) ===
  // Inicializamos el carrito vacío
  cartItems = signal<CartItem[]>([]);
  discountPercentage = signal<number>(0);
  couponCode = signal<string | null>(null);

  // === ESTADO DERIVADO (Computed Signals) ===
  // Subtotal sin descuentos
  subtotal = computed(() => {
    return this.cartItems().reduce((total, item) => total + (item.price * item.quantity), 0);
  });

  // Monto del descuento calculado
  discountAmount = computed(() => {
    return (this.subtotal() * this.discountPercentage()) / 100;
  });

  // Calcula el total final a pagar automáticamente aplicando descuentos
  cartTotal = computed(() => {
    const total = this.subtotal() - this.discountAmount();
    return Math.max(0, total);
  });

  // Calcula la cantidad total de artículos (ideal para el numerito rojo del Header)
  cartCount = computed(() => {
    return this.cartItems().reduce((count, item) => count + item.quantity, 0);
  });

  // === GESTIÓN DE DESCUENTOS Y CUPONES ===
  applyCoupon(code: string, percent: number = 10) {
    this.couponCode.set(code.toUpperCase().trim());
    this.discountPercentage.set(percent);
  }

  removeCoupon() {
    this.couponCode.set(null);
    this.discountPercentage.set(0);
  }

  // === MÉTODOS ===

  // Añadir un producto al carrito
  addToCart(product: Product, quantity: number = 1) {
    this.cartItems.update(items => {
      const existingItem = items.find(item => item.id === product.id);

      // Si el producto ya está en el carrito, solo sumamos 1 a la cantidad
      if (existingItem) {
        return items.map(item =>
          item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
        );
      }

      // Si no está, lo agregamos como un elemento nuevo con cantidad 1
      return [...items, { ...product, quantity: quantity }];
    });
  }

  // Actualizar cantidad (+ o -) desde la página del carrito
  updateQuantity(productId: string, delta: number) {
    this.cartItems.update(items => {
      return items.map(item => {
        if (item.id === productId) {
          // Aseguramos que la cantidad nunca sea menor a 1
          const newQuantity = Math.max(1, item.quantity + delta);
          return { ...item, quantity: newQuantity };
        }
        return item;
      });
    });
  }

  // Eliminar producto completamente del carrito
  removeFromCart(productId: string) {
    this.cartItems.update(items => items.filter(item => item.id !== productId));
  }

  // Vaciar el carrito (útil para después de que el usuario finaliza su compra)
  clearCart() {
    this.cartItems.set([]);
  }
}
